import React, { useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist";
import "../sass/brochure.scss";

// Use the worker copied into /public (matches the installed pdfjs-dist version)
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

// The brochure lives in /public/brochures so it is served as a static file.
const BROCHURE_URL = `${process.env.PUBLIC_URL}/brochures/Ishan-Corporate-Brochure_19.08.2026.pdf`;

// A single page of the flip book. react-pageflip requires each page to be a
// component that forwards its ref, so it can attach flip handlers to the DOM node.
const Page = React.forwardRef(({ image, number }, ref) => (
  <div className="brochure-page" ref={ref}>
    <img src={image} alt={`Brochure page ${number}`} draggable={false} />
  </div>
));
Page.displayName = "BrochurePage";

export default function Brochure() {
  const [pages, setPages] = useState([]); // array of image data-URLs
  const [ratio, setRatio] = useState(1.414); // page height / width (A4 default)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dims, setDims] = useState({ width: 450, height: 636 });
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const flipBook = useRef(null);

  // Render every PDF page to an image once, on mount.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument(BROCHURE_URL).promise;
        const images = [];
        let pageRatio = 1.414;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 2 }); // scale 2 = crisp
          if (i === 1) pageRatio = viewport.height / viewport.width;

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          // willReadFrequently keeps rendering on the CPU path, which we read
          // back immediately via toDataURL().
          const context = canvas.getContext("2d", { willReadFrequently: true });
          await page.render({ canvasContext: context, viewport }).promise;
          images.push(canvas.toDataURL("image/jpeg", 0.85));

          if (cancelled) return;
        }

        if (!cancelled) {
          setRatio(pageRatio);
          setPages(images);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the book sized to the viewport.
  useEffect(() => {
    const resize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On desktop the book shows two pages side-by-side, so each page can be
      // narrower; on mobile it shows one page.
      const maxPerPage = mobile
        ? Math.min(window.innerWidth - 40, 480)
        : Math.min((window.innerWidth - 120) / 2, 480);
      const width = Math.max(260, maxPerPage);
      setDims({ width, height: Math.round(width * ratio) });
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [ratio]);

  const prev = () => flipBook.current?.pageFlip()?.flipPrev();
  const next = () => flipBook.current?.pageFlip()?.flipNext();

  return (
    <div className="brochure-viewer">
      <div className="brochure-head">
        <h1>Corporate Brochure</h1>
        <a
          className="brochure-download"
          href={BROCHURE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download PDF
        </a>
      </div>

      {loading && (
        <div className="brochure-status">Loading brochure…</div>
      )}

      {error && (
        <div className="brochure-status">
          Sorry, the brochure could not be loaded.{" "}
          <a href={BROCHURE_URL} target="_blank" rel="noopener noreferrer">
            Open the PDF instead
          </a>
          .
        </div>
      )}

      {!loading && !error && pages.length > 0 && (
        <>
          <div className="brochure-book-wrap">
            <HTMLFlipBook
              width={dims.width}
              height={dims.height}
              size="fixed"
              minWidth={260}
              maxWidth={480}
              minHeight={300}
              maxHeight={700}
              showCover={true}
              mobileScrollSupport={true}
              usePortrait={isMobile}
              maxShadowOpacity={0.5}
              className="brochure-flipbook"
              ref={flipBook}
            >
              {pages.map((image, i) => (
                <Page key={i} image={image} number={i + 1} />
              ))}
            </HTMLFlipBook>
          </div>

          <div className="brochure-controls">
            <button onClick={prev} aria-label="Previous page">
              ‹ Prev
            </button>
            <button onClick={next} aria-label="Next page">
              Next ›
            </button>
          </div>
        </>
      )}
    </div>
  );
}
